import { TranslationKey } from './../../../../../../types/translation/TranslationKey';
import { Grid } from '@mui/material';
import React, { useContext, useMemo, useState } from 'react';
import { t } from '../../../../../../types/translation/Translator';
import DropdownSelect from '../../../../Common/DropdownSelect';
import { ProductTransactionSpecifiers } from '../../../../../../types/productTransaction';
import { TestIdIdentifier, testIds } from '../../../../../../util/identifiers/identifiers.util';
import { CustomFieldContext } from '../../../../../../context/CustomFieldContext';
import { ModalPane } from '../../../../../../VentoryUI/components/common/Modal/components';
import { Button } from '../../../../../../VentoryUI/components/common/Button/Button';
import DeleteButton from '../../../../../../VentoryUI/components/common/Button/Templates/DeleteButton';
import Table, { TableHeader } from '../../../../../components/Table/Table';
import { FlexPane } from '../../../../../../VentoryUI/components/common/FlexPane/FlexPane';
import { ContainerContext } from '../../../../../../context/ContainerContext';
import { Container } from '../../../../../../types/container';
import { ContainerTypeContext } from '../../../../../../context/ContainerTypeContext';
import BoxIcon from '../../../../../../VentoryUI/icons/Box/BoxIcon';
import { toGroupedMap, toMap } from '../../../../../../util/map.util';
import { ProductContext } from '../../../../../../context/ProductContext';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';
import PackageIcon from '../../../../../../VentoryUI/icons/Package/PackagIcon';
import TextInput from '../../../../Common/TextInput';
import { cloneDeep } from '@apollo/client/utilities';
import Checkbox from '../../../../../../VentoryUI/components/common/Checkbox/Checkbox';
import CreateOrderCreateContainerModal from './Modals/CreateOrderCreateContainerModal';
import CreateButton from '../../../../../../VentoryUI/components/common/Button/Templates/CreateButton';
import { ChevronDownIcon } from '../../../../../../VentoryUI/icons/ChevronDown/ChevronDownIcon';
import { ChevronRightIcon } from '../../../../../../VentoryUI/icons/ChevronRight/ChevronRightIcon';
import { ContainerUtil } from '../../../../../../util/containers.util';
import { OrderInput, OrderUtil } from '../../../../../../types/order/';
import { UnitOfMeasureUtil } from '../../../../../../types/uom';
import { BinContext } from '../../../../../../context/BinContext';
import { BinStatusContext } from '../../../../../../context/BinStatusContext';
import { UserContext } from '../../../../../../context/UserContext';
import { FlowType } from '../../../../../../types/order/orderFlow';

interface CreateOrderContainerPaneProps {
  order: OrderInput;
  setOrder: (order: OrderInput) => void;
  next?: () => void;
  back?: () => void;
  add?: () => void;
  cancel?: () => void;
  loading?: boolean;
  setError: (err: string) => void;
  validContainers: Container[];
}

export default function CreateOrderContainerPane({
  order,
  setOrder,
  next,
  back,
  add,
  cancel,
  setError,
  loading = false,
  validContainers,
}: CreateOrderContainerPaneProps) {
  const { containers, containersLoading } = useContext(ContainerContext);
  const { customFields } = useContext(CustomFieldContext);
  const { containerTypes } = useContext(ContainerTypeContext);
  const { locale } = useContext(UserContext);
  const { bins } = useContext(BinContext);
  const { binStatuses } = useContext(BinStatusContext);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [customFieldDatePickerOpen, setCustomFieldDatePickerOpen] = useState(false);
  const [createContainerModalOpen, setCreateContainerModalOpen] = useState(false);

  const orderContainers = useMemo(() => {
    return [...order.products.values()].filter(p => !!p.containerId);
  }, [order]);

  const headers: TableHeader<ProductTransactionSpecifiers>[] = [
    {
      key: 'name',
      name: t(TranslationKey.name).singular.label,
      text: (item: ProductTransactionSpecifiers) => {
        return (
          <div className='flex content-center'>
            <BoxIcon />
            <p className='pl-2 text-[12px] font-[400] text-gray-900'>
              {containers.get(item.containerId || '')?.identifier || t(TranslationKey.unknownContainer).singular.label}
            </p>
          </div>
        );
      },
    },
    {
      key: 'type',
      name: t(TranslationKey.containerType).singular.label,
      text: (item: ProductTransactionSpecifiers) => {
        const container = containers.get(item.containerId || '');
        const containerType = containerTypes.get(container?.containerTypeId || '');
        if (!containerType) return t(TranslationKey.unknownContainerType).singular.label;
        return containerType.name;
      },
    },
  ].filter(e => e != null) as TableHeader<ProductTransactionSpecifiers>[];

  const availableContainers = useMemo(() => {
    switch (order.type) {
      case FlowType.inbound:
      case FlowType.replenish:
        return validContainers.filter(container => {
          return !order.products.has(OrderUtil.orderSpecifierHashByValue(undefined, container.id));
        });
      case FlowType.outbound:
      case FlowType.move:
      case FlowType.disposal:
        const familyContainerIds = new Set(
          [...order.products.values()]
            .filter(p => p.containerId)
            .map(p => [
              ...ContainerUtil.getAllChildContainers(containers, p.containerId!),
              ...ContainerUtil.getAllParentContainers(containers, p.containerId!),
            ])
            .flat()
            .map(c => c.id),
        );

        return validContainers.filter(container => {
          const outboundAllowed =
            binStatuses.get(bins.get(container.binId || '')?.binStatusId || '')?.outboundAllowed ?? true;
          if (order.type === FlowType.disposal && outboundAllowed) return false;
          else if (order.type !== FlowType.disposal && !outboundAllowed) return false;

          return (
            !order.products.has(OrderUtil.orderSpecifierHashByValue(undefined, container.id)) &&
            !familyContainerIds.has(container.id)
          );
        });
    }
  }, [containers, order]);

  return (
    <>
      {createContainerModalOpen ? (
        <CreateOrderCreateContainerModal
          onCreated={container => {
            setOrder(order.withContainer(container.id));
          }}
          open={createContainerModalOpen}
          setOpen={setCreateContainerModalOpen}
        />
      ) : null}

      <ModalPane
        overflow='hidden'
        testId={testIds.createOrderProductPane}
        footer={
          <Grid container>
            <Grid item>
              <Grid container columnSpacing={1}>
                {selected.size ? (
                  <Grid item>
                    <DeleteButton
                      loading={loading}
                      onClick={() => {
                        setError('');
                        const containerIds = new Set(
                          [...selected.values()]

                            .map(s => s.split('|'))
                            .filter(s => !s[0] && !!s[1])
                            .map(s => s[1]!),
                        );

                        const newProducts = [...order.products.values()].filter(t => {
                          const hash = OrderUtil.orderSpecifierHash(t);
                          if (selected.has(hash)) return false;
                          if (t.toContainerId && containerIds.has(t.toContainerId)) return false;
                          return true;
                        });

                        order.products = toMap(newProducts, item => OrderUtil.orderSpecifierHash(item));
                        setOrder(cloneDeep(order));
                        setSelected(new Set());
                      }}
                    />
                  </Grid>
                ) : null}
              </Grid>
            </Grid>
            <Grid item flexGrow={1}>
              <Grid container columnSpacing={1} justifyContent={'flex-end'}>
                {back ? (
                  <Grid item>
                    <Button
                      onClick={back}
                      testId={testIds.back}
                      text={t(TranslationKey.back).singular.label}
                      disabled={loading}
                    />
                  </Grid>
                ) : null}
                {next ? (
                  <Grid item>
                    <Button
                      style='secondary'
                      loading={loading}
                      onClick={next}
                      testId={testIds.next}
                      text={
                        !order.fromStockLocationId?.includes('tag::')
                          ? t(TranslationKey.next).singular.label
                          : t(TranslationKey.done).singular.label
                      }
                    />
                  </Grid>
                ) : null}
                {cancel ? (
                  <Grid item>
                    <Button disabled={loading} onClick={cancel} text={t(TranslationKey.cancel).singular.label} />
                  </Grid>
                ) : null}
                {add ? (
                  <Grid item>
                    <Button
                      style='secondary'
                      loading={loading}
                      disabled={!order.products.size}
                      onClick={add}
                      testId={testIds.add}
                      text={t(TranslationKey.add).singular.label}
                    />
                  </Grid>
                ) : null}
              </Grid>
            </Grid>
          </Grid>
        }
      >
        <FlexPane
          contentOverflow={'auto'}
          header={
            <Grid container className='content-center' gap={2}>
              <Grid item flexGrow={1}>
                <DropdownSelect
                  disabled={containersLoading || loading}
                  selectOnly
                  testId={testIds.container}
                  values={availableContainers}
                  selectedValue={null}
                  toText={item => {
                    return item.identifier || '';
                  }}
                  toElement={item => {
                    const containerType = containerTypes.get(item.containerTypeId);

                    return (
                      <Grid container>
                        <Grid item className='h-full content-center pr-4'>
                          <BoxIcon />
                        </Grid>
                        <Grid item flexGrow={1}>
                          <Grid container>
                            <Grid item xs={12}>
                              <p className='text-sm font-bold'>{item.identifier}</p>
                            </Grid>
                            <Grid item my={'auto'} xs={12}>
                              <p className='text-sm text-gray-500'>{`${containerType?.name}`}</p>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    );
                  }}
                  onChange={item => {
                    if (!item) return;

                    setOrder(order.withContainer(item.id));
                  }}
                  placeholder={t(TranslationKey.selectContainer).singular.upper}
                />
              </Grid>
              {order.type === FlowType.inbound ? (
                <Grid item className='content-center'>
                  <div className='mt-1'>
                    <CreateButton onClick={() => setCreateContainerModalOpen(true)} />
                  </div>
                </Grid>
              ) : null}
            </Grid>
          }
          content={
            order.type === FlowType.outbound || order.type === FlowType.disposal || order.type === FlowType.move ? (
              <Table<ProductTransactionSpecifiers>
                modal
                uniqueIdentifier={item => OrderUtil.orderSpecifierHash(item)}
                loading={containersLoading}
                onSelected={items => setSelected(new Set(items))}
                selectedValues={selected}
                items={orderContainers}
                headers={headers}
              />
            ) : (
              <InboundContainerList order={order} setOrder={setOrder} selected={selected} setSelected={setSelected} />
            )
          }
          testId={testIds.createOrderProductPane}
        />
      </ModalPane>
    </>
  );
}

interface InboundContainerListProps {
  order: OrderInput;
  setOrder: (order: OrderInput) => void;
  selected: Set<string>;
  setSelected: (selected: Set<string>) => void;
}

function InboundContainerList({ order, setOrder, selected, setSelected }: InboundContainerListProps) {
  const containerSpecifiers = [...order.products.values()].filter(p => p.containerId);

  const byToContainerId = useMemo(() => {
    return toGroupedMap([...order.products.values()], 'toContainerId');
  }, [order]);

  return (
    <div className='grid gap-y-2'>
      {containerSpecifiers.map(specifier => {
        return (
          <InboundContainerItem
            key={OrderUtil.orderSpecifierHash(specifier)}
            order={order}
            setOrder={setOrder}
            specifier={specifier}
            specifiers={byToContainerId.get(specifier.containerId || '') || []}
            selected={selected}
            setSelected={setSelected}
          />
        );
      })}
    </div>
  );
}

interface InboundContainerItemProps {
  order: OrderInput;
  setOrder: (order: OrderInput) => void;
  specifier: ProductTransactionSpecifiers;
  specifiers: ProductTransactionSpecifiers[];
  selected: Set<string>;
  setSelected: (selected: Set<string>) => void;
}

function InboundContainerItem({
  order,
  setOrder,
  specifier,
  specifiers,
  selected,
  setSelected,
}: InboundContainerItemProps) {
  const containerId = specifier.containerId;
  if (!containerId) return null;
  const { containers } = useContext(ContainerContext);
  const { locale } = useContext(UserContext);

  const [open, setOpen] = useState(true);

  const container = containers.get(containerId);
  if (!container) return null;

  const specifiersByPmdId = toGroupedMap(specifiers, 'pmdId');

  const { productMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { products, productsLoading } = useContext(ProductContext);

  const headers: TableHeader<ProductTransactionSpecifiers>[] = [
    {
      key: 'name',
      name: t(TranslationKey.name).singular.label,
      text: (item: ProductTransactionSpecifiers) => {
        return (
          <div className='flex content-center'>
            <PackageIcon />
            <p className='pl-2 text-[12px] font-[400] text-gray-900'>
              {productMasterData.get(item.pmdId || '')?.productName ||
                t(TranslationKey.unknownProductReference).singular.label}
            </p>
          </div>
        );
      },
    },
    {
      key: 'quantity',
      name: t(TranslationKey.quantity).singular.label,
      text: (item: ProductTransactionSpecifiers) => (
        <Grid container className='h-7' justifyContent={'flex-end'}>
          <Grid item xs={6} marginY={'auto'} justifyContent={'flex-end'} mr={0.1}>
            <TextInput
              disabled={!!item.containerId}
              onChange={v => {
                const foundItem = order.products.get(OrderUtil.orderSpecifierHash(item) || '');
                if (!foundItem) return;
                foundItem.quantity = parseFloat(v) || 0;
                setOrder(cloneDeep(order));
              }}
              value={item.quantity.toString()}
              dynamicUpdate
              testId={new TestIdIdentifier(`quantityInput${OrderUtil.orderSpecifierHash(item)}`)}
              heightClass='h-7'
              type='number'
              // Making the suffix a space by default so the text input will display the suffix version even if the UnitOfMeasure is quantity.
              suffix={UnitOfMeasureUtil.getSuffix(productMasterData.get(item.pmdId || '')?.unitOfMeasure) || ' '}
            />
          </Grid>
        </Grid>
      ),
      sortValue: (item: ProductTransactionSpecifiers) => item.quantity,
    },
  ].filter(e => e != null) as TableHeader<ProductTransactionSpecifiers>[];

  const hash = OrderUtil.orderSpecifierHash(specifier);

  return (
    <Grid container className='rounded border border-slate-300 p-4' gap={1}>
      <Grid item xs={12} onClick={() => setOpen(!open)}>
        <Grid container columnGap={1}>
          <Grid item>
            <Checkbox
              value={selected.has(hash)}
              onChange={value => {
                if (value) {
                  selected.add(hash);
                } else {
                  selected.delete(hash);
                }
                setSelected(new Set(selected));
              }}
            />
          </Grid>
          <Grid item className='content-center'>
            <BoxIcon />
          </Grid>
          <Grid item>
            <p className='text-[14px] text-gray-900'>{container.identifier}</p>
          </Grid>
          <Grid item flexGrow={1}></Grid>
          <Grid item className='content-center'>
            <p className='mr-2 text-[14px] text-gray-900'>
              {specifiersByPmdId.size}{' '}
              {specifiersByPmdId.size === 1
                ? t(TranslationKey.product).singular.label
                : t(TranslationKey.product).plural.label}
            </p>
          </Grid>
          <Grid item className='flex content-center'>
            {open ? <ChevronDownIcon /> : <ChevronRightIcon />}
          </Grid>
        </Grid>
      </Grid>

      {open ? (
        <>
          <Grid item xs={12}>
            <DropdownSelect
              disabled={productMasterDataLoading || productsLoading}
              selectOnly
              testId={testIds.product}
              values={[...productMasterData.values()].filter(pmd => !specifiersByPmdId.has(pmd.id))}
              selectedValue={null}
              toText={item => {
                return `${item?.productName} - ${item.productNumber}` || '';
              }}
              toElement={item => {
                // const quantity = quantityForProduct.get(item.id);
                return (
                  <Grid container>
                    <Grid item className='h-full content-center pr-4'>
                      <PackageIcon />
                    </Grid>
                    <Grid item flexGrow={1}>
                      <Grid container>
                        <Grid item xs={12}>
                          <p className='text-sm font-bold'>{item.productName}</p>
                        </Grid>
                        <Grid item my={'auto'} xs={12}>
                          <p className='text-sm text-gray-500'>{item.productNumber}</p>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                );
              }}
              onChange={item => {
                if (!item) return;
                setOrder(order.withProduct(item.id, undefined, containerId));
              }}
              placeholder={t(TranslationKey.selectProduct).singular.upper}
            />
          </Grid>

          {specifiers.length ? (
            <Grid item xs={12}>
              <Table
                modal
                uniqueIdentifier={item => OrderUtil.orderSpecifierHash(item)}
                selectedValues={selected}
                onSelected={setSelected}
                items={specifiers}
                headers={headers}
                customPagination={5}
              />
            </Grid>
          ) : null}
        </>
      ) : null}
    </Grid>
  );
}
